<!-- eslint-disable max-len -->
<template>
  <div class="goal-card" :class="getStatusClass()">
    <div class="goal-header">
      <div class="goal-info">
        <div class="goal-icon">
          <div class="icon-placeholder">
            <i :class="'fas ' + getIconClass(goal.goal_type)"></i>
          </div>
        </div>
        <div class="goal-details">
          <h4 class="goal-title cabin-family semi-bold">{{ goal.title }}</h4>
          <p class="goal-category secondary-text">{{ goal.goal_type ? goal.goal_type.name : 'Goal' }}</p>
        </div>
      </div>

      <div v-if="latestBadge" class="badge-container">
        <div :class="`badge badge-${latestBadge.badge_type}`">
          <i class="fas fa-trophy"></i>
          {{ latestBadge.badge_type }}
        </div>
      </div>
    </div>

    <div class="progress-section">
      <div class="progress-info">
        <span class="current-amount cabin-family semi-bold primary">
          {{ formatCurrency(goal.current_amount || 0) }}
        </span>
        <span class="target-amount secondary-text">
          of {{ formatCurrency(goal.target_amount || 0) }}
        </span>
      </div>

      <ProgressBar
        v-if="goal.progress_percentage !== null && goal.progress_percentage !== undefined"
        :percentage="goal.progress_percentage"
        :color="getProgressColor()"
        show-label
        height="10px"
      />

      <div v-if="goal.time_progress_percentage !== undefined" class="time-progress">
        <div class="time-info">
          <span class="secondary-text fnt-10">Time Progress</span>
          <span class="secondary-text fnt-10">{{ Math.round(goal.time_progress_percentage || 0) }}%</span>
        </div>
        <ProgressBar
          v-if="goal.time_progress_percentage !== null && goal.time_progress_percentage !== undefined"
          :percentage="goal.time_progress_percentage"
          color="neutral"
          height="4px"
        />
      </div>
    </div>

    <div class="goal-stats">
      <div class="stat">
        <span class="stat-label secondary-text fnt-10">Days Remaining</span>
        <span
          class="stat-value cabin-family semi-bold"
          :class="{ 'urgent': daysRemaining <= 30 }"
        >
          {{ daysRemaining }}
        </span>
      </div>

      <div class="stat">
        <span class="stat-label secondary-text fnt-10">Status</span>
        <span
          class="stat-value cabin-family semi-bold"
          :class="{ 'on-track': goal.is_on_track, 'behind': !goal.is_on_track }"
        >
          {{ goal.is_on_track ? 'On Track' : 'Behind' }}
        </span>
      </div>
    </div>

    <div class="goal-actions">
      <button
        class="btn primary-btn flex-fill me-2"
        :disabled="isCompleted"
        @click="$emit('contribute', goal)"
      >
        {{ isCompleted ? 'Completed' : 'Contribute' }}
      </button>

      <button
        class="btn btn-outline-secondary flex-fill"
        @click="$emit('view-details', goal)"
      >
        View Details
      </button>
    </div>
  </div>
</template>

<script>
import { formatCurrency, daysBetween } from '../../utils/formatters';
import { ProgressBar } from '../shared';

export default {
  name: 'GoalCard',
  components: {
    ProgressBar,
  },
  props: {
    goal: {
      type: Object,
      required: true,
    },
  },
  computed: {
    isCompleted() {
      return (this.goal.progress_percentage || 0) >= 100;
    },

    daysRemaining() {
      if (!this.goal.target_date) return 0;
      const today = new Date();
      const targetDate = new Date(this.goal.target_date);
      const days = daysBetween(today, targetDate);
      return targetDate < today ? -days : days;
    },

    latestBadge() {
      return this.goal.goal_badges && this.goal.goal_badges.length > 0
        ? this.goal.goal_badges[0]
        : null;
    },
  },
  methods: {
    formatCurrency,

    getStatusClass() {
      if (this.isCompleted) return 'status-success';
      if (this.goal.is_on_track) return 'status-primary';
      if (this.daysRemaining <= 30) return 'status-warning';
      return 'status-danger';
    },

    getProgressColor() {
      if (this.isCompleted) return 'success';
      if (this.goal.is_on_track) return 'primary';
      if (this.daysRemaining <= 30) return 'warning';
      return 'danger';
    },

    getIconClass(goalType) {
      const category = goalType && (goalType.icon || goalType.category);
      const map = {
        savings: 'fa-piggy-bank',
        debt_reduction: 'fa-credit-card',
        investment: 'fa-chart-line',
        emergency_fund: 'fa-shield-halved',
        education: 'fa-graduation-cap',
        business: 'fa-briefcase',
        vacation: 'fa-plane',
        home: 'fa-house',
        car: 'fa-car',
        retirement: 'fa-umbrella-beach',
      };
      return map[category] || 'fa-bullseye';
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../scss/variable.scss';

.goal-card {
  background: $white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  border: 1px solid #E0E0E0;
  margin-bottom: 1rem;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    border-color: $primary;
  }

  &.status-success {
    border-left: 6px solid $success;
  }

  &.status-primary {
    border-left: 6px solid $primary;
  }

  &.status-warning {
    border-left: 6px solid #FF9800;
  }

  &.status-danger {
    border-left: 6px solid #F44336;
  }
}

.goal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.goal-info {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
}

.goal-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: $primary-light;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  .icon-image {
    width: 24px;
    height: 24px;
    object-fit: contain;
  }

  .icon-placeholder {
    color: $primary;
    font-size: 20px;
  }
}

.goal-details {
  flex: 1;
  min-width: 0;
}

.goal-title {
  margin: 0 0 0.25rem 0;
  font-size: 16px;
  line-height: 1.2;
  color: $black;
}

.goal-category {
  margin: 0;
  font-size: 12px;
  text-transform: capitalize;
}

.badge-container {
  flex-shrink: 0;
}

.badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  border-radius: 16px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;

  &.badge-bronze {
    background: #CD7F32;
    color: white;
  }

  &.badge-silver {
    background: #C0C0C0;
    color: #333;
  }

  &.badge-gold {
    background: #FFD700;
    color: #333;
  }

  &.badge-platinum {
    background: #E5E4E2;
    color: #333;
  }
}

.progress-section {
  margin-bottom: 1rem;
}

.progress-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.current-amount {
  font-size: 18px;
}

.target-amount {
  font-size: 14px;
}

.time-progress {
  margin-top: 0.75rem;
}

.time-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.25rem;
}

.goal-stats {
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
  padding: 1rem;
  background: rgba(248, 249, 250, 0.5);
  border-radius: 8px;
  border: 1px solid #F0F0F0;
}

.stat {
  text-align: center;
  flex: 1;
}

.stat-label {
  display: block;
  margin-bottom: 0.25rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stat-value {
  font-size: 16px;
  color: $black;

  &.urgent {
    color: $primary;
  }

  &.on-track {
    color: $success;
  }

  &.behind {
    color: $primary;
  }
}

.goal-actions {
  display: flex;
  gap: 0.5rem;
}

.btn-outline-secondary {
  border: 1px solid $secondary;
  color: $secondary;
  background: transparent;

  &:hover {
    background: $secondary;
    color: $white;
  }
}

@media (max-width: 576px) {
  .goal-card {
    padding: 1rem;
  }

  .goal-info {
    gap: 0.75rem;
  }

  .goal-icon {
    width: 40px;
    height: 40px;

    .icon-image {
      width: 20px;
      height: 20px;
    }
  }

  .goal-title {
    font-size: 14px;
  }

  .current-amount {
    font-size: 16px;
  }

  .goal-actions {
    flex-direction: column;
    gap: 0.5rem;
  }
}
</style>
